import * as React from "react";
import { useState, useContext, createContext, useEffect } from "react";
import { makeStyles, Theme, createStyles } from "@mui/styles";
import {ValueContext} from '~common/components/form-def-field-fc';
import {CircularProgress} from '@mui/material';
import Dropzone from '~common/components/versioned-document-dropzone';
import * as request from 'superagent';
import PortalUserContext from '~common/lib/portal-user-context';
import {cloneDeep} from 'lodash'
import { red, green, grey } from '@mui/material/colors';
import { v4 as uuidv4 } from 'uuid';
import {
    FormControl,
    Select,
    MenuItem,
    Tooltip,
    IconButton,
    Icon
} from "@mui/material";
import classNames from 'classnames';
import ImageViewer from '~common/components/fields/image-viewer-fc';
import {CurrentAttachmentContext} from "~common/form/icg-definition-data-provider";

var MAX_FILE_SIZE = 5242880;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
        marginRight: 10,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
    },
    selector: {
        width: 300
    },
    controls: {
        marginBottom: theme.spacing(2) + " !important",
    },
    emptySet: {
        width: '100%',
        height: '500px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid #ddd',
    },
    dropzone: {
        display: 'flex',
        alignItems: 'center',
        backgroundColor: grey[100],
        marginLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    },
    dropzoneError: {
        display: 'flex',
        alignItems: 'center',
        marginLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        backgroundColor: red[100],
    },
    dropzoneWarn: {
        display: 'flex',
        alignItems: 'center',
        marginLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        backgroundColor: grey[100],
        border: "1px dashed #ffe002 !important"
    },
    dropzoneHistory: {
        display: 'flex',
        alignItems: 'center',
        marginLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        backgroundColor: grey[100],
        border: "1px dashed #1614b3 !important",
    },
  })
);

interface ImageListProps {
    fieldDefinition: any;
    policyMessages?: any;
    readOnlyOverride?: boolean;
    path?: string;
}

//export const CurrentAttachmentContext = createContext();

export const ImageList: React.FC<ImageListProps> = ({
    fieldDefinition,
    policyMessages,
    readOnlyOverride,
    path
  }) => {
    const classes = useStyles();  

    var parentContext = ValueContext
    
    const [fieldValue, setFieldValue] = useContext(parentContext); 
    const [currentAttachment, setCurrentAttachment, currentPage, setCurrentPage] = useContext(CurrentAttachmentContext); 
    //const [currentAttachment, setCurrentAttachment] = useState(undefined)
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(undefined);
    const [listOptions, setListOptions] = useState(fieldValue ? fieldValue.list : undefined)

    let readOnly: boolean = (fieldDefinition.mode == "Read Only" || fieldDefinition.mode == "Display") ? true : false
    if (readOnlyOverride) {
      readOnly = true
    }

    var hasHistoryPolicy: boolean = false

    if (policyMessages && policyMessages.length > 0) {
        policyMessages.forEach((pm:any)=>{
            if (pm.path && pm.path.indexOf(path) > -1 && pm.severity == 4) {
                hasHistoryPolicy = true
            }
        })
    }

    const onDocumentSelectionChange = (e: any) => {
        if (e && e.target && e.target.value) {
            listOptions.forEach((o: any)=>{
                if (o.active.id == e.target.value) {
                    setCurrentAttachment(o.active)
                    return
                }
            })
        }
    }

    const getHistoryPolicy = (idx: number) => {
        var historyEntry
        (policyMessages||[]).forEach((pm:any)=>{
            if (pm.path && pm.path.indexOf(path) > -1 && pm.severity == 4) {
                var parts = pm.path.split("/")
                var lastPart = parseInt(parts[parts.length -1])
                if (!isNaN(lastPart) && lastPart == idx) {
                    historyEntry = pm
                }
            }
        })
        return historyEntry
    }

    const onDrop = (acceptedFiles: Array<any>, rejectedFiles: Array<any>) => {
        
        if (!uploading) {

            if (rejectedFiles && rejectedFiles.length > 0) {

                setUploading(false)
    
                rejectedFiles.forEach((f: any) => {
                    // react-dropzone sends response files in f.file.size
                    if (f.file && f.file.size > (fieldDefinition.maxSize || MAX_FILE_SIZE)) {
    
                        setError(`File size cannot exceed ${(
                            (fieldDefinition.maxSize || MAX_FILE_SIZE) /
                            (1024 * 1024)
                        ).toFixed(0)}MB`)
    
                    } else {
                        setError("Invalid file type")
                    }
                    return
                });
            }
    
            if (acceptedFiles && acceptedFiles.length > 0) {
    
                setError(undefined)  
    
                var fv = cloneDeep(fieldValue);
    
                if (!fv || !fv.list) {
                    fv = {
                        list: []
                    }
                } 
    
                // Ensure the maxCombinedSize is not going to be exceeded with this attachment
                if ((fieldDefinition.maxCombinedSize || 0) > 0) {
                    // Prime the combined size (cs) with the file about to be added
                    var cs = acceptedFiles[0].size;
                    for (let f of (fv.list|| [])) {
                        if (f && f.active) {
                            cs += (f.active.size || 0)
                        }
                    }
                    // If the combined size exceeds the maxCombinedSize, abort and notify user
                    if (cs > fieldDefinition.maxCombinedSize) {
                        setError(`Combined file size cannot exceed ${(
                            (fieldDefinition.maxCombinedSize) /
                            (1024 * 1024)
                        ).toFixed(0)}MB`)
                        return
                    }
                }
    
                setUploading(true)
    
                for (let f of acceptedFiles) {
                    let parseFlag = `?opts={"forcePageCount":true, "extractPages":true, "density":200, "monochrome":true}`
                    if (f.type.indexOf("vnd.openxmlformats-officedocument") > -1) {
                        parseFlag = '?parse=false';
                    }
                    request
                        .post((`/api/attachment-storage`) + parseFlag)
                        .attach(f.name, f)
                        .then((response) => {
                            if (response.ok) {
                                const att = {
                                    id: uuidv4(),
                                    fileName: f.name,
                                    contentType: f.type,
                                    size: f.size,
                                    pageCount: response.body.pageCount,
                                    storageKey: response.body.storageKey,
                                    data: response.body,
                                    uploadedOn: new Date(),
                                    uploadedBy: {
                                        name: PortalUserContext.userContext.displayName,
                                        email: PortalUserContext.userContext.email
                                    },
                                    _new: true
                                };
    
                                fv.list = fv.list || [] 
    
                                if (fv.list) {
                                    fv.list.push({ active: att });
                                }
                                
                                setUploading(false)
    
                                setCurrentAttachment(att)
    
                                setFieldValue(fv)
    
                            } else {
                                // Report the error to the user
                                setUploading(false)
                                setError('An error occurred uploading the document')
                
                            }
                        })
                        .catch((err) => {
                            setUploading(false)
                            setError('An error occurred uploading the document')
        
                        });
                }
                
            }

        }
       
    }

    const removeAttachment = () => {
        let newList: Array<any> = [];

        if (listOptions) {

            listOptions.forEach((o: any)=>{
                if (o.active.id != currentAttachment.id) {
                    newList.push(o);
                }
            })

            setFieldValue({list: newList})
        }
    
    }

    var prompt: any = 'Drop file to upload new document'

    if (fieldDefinition.helpText) {
        prompt = fieldDefinition.helpText
    }

    let dzClass = classes.dropzone
    if (policyMessages && policyMessages.length > 0) {
        
        prompt = policyMessages[0].message;

        if (policyMessages[0].title == "Field required") {
            prompt = "At least one document is required"
        }

        switch (policyMessages[0].severity) {
            case 1:
                dzClass = classes.dropzoneWarn;
                break;
            case 2:
                dzClass = classes.dropzoneError;
                break;
            case 4:
                dzClass = classes.dropzoneHistory;
                break
          }
    }

    if (error) {
        prompt = error
        dzClass = classes.dropzoneError;
    }

    var dropZone: any = (
        <Dropzone
            multiple={false}
            minSize={fieldDefinition.minSize}
            maxSize={fieldDefinition.maxSize || MAX_FILE_SIZE}
            accept={fieldDefinition.accept || 'image/gif, image/jpeg, application/pdf, image/png, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, image/tiff, text/plain, text/csv, application/vnd.ms-outlook, message/rfc822, .msg'}
            onDrop={onDrop}
            disabled={readOnly}
            inputProps={{ tabIndex: fieldDefinition.tabIndex || -1 }}
        >
            <div className={dzClass}>
                
                <Tooltip tabIndex={-1} key="Upload" title="Upload New Document">
                    <IconButton name="uploadNewDocument" size="large">
                        {!uploading && <Icon>backup</Icon>}
                        {uploading && <CircularProgress color={'inherit'} size={20} thickness={3} />}
                    </IconButton>
                </Tooltip>

                <div>
                    {prompt}
                </div>
            </div>
        </Dropzone>
    )

    useEffect(() => {
        if (fieldValue && fieldValue.list && fieldValue.list.length > 0) {
            var ca = cloneDeep(fieldValue.list[0])
            if (ca) {
                setCurrentAttachment(ca.active)
            }
        }
    }, [fieldValue])

    useEffect(() => {
        var lo
        if (fieldValue && fieldValue.list) {
            lo = cloneDeep(fieldValue.list)
        }

        if (lo && listOptions && lo.length < listOptions.length) {
            if (currentAttachment) {

                var currentAttachmentIndex = lo.findIndex((i:any)=>{
                    return i.id == currentAttachment.id
                })
                var newCurrentAttachmentIndex

                if (currentAttachmentIndex == -1) {
                    newCurrentAttachmentIndex = lo.length -1
                }

                if (newCurrentAttachmentIndex > -1) {
                    var ca = cloneDeep(lo[newCurrentAttachmentIndex])
                    setCurrentAttachment(ca.active)
                }
            }
        }

        if ( (lo && listOptions && lo.length > listOptions.length) || (lo && !listOptions)) {
            var ca = cloneDeep(lo[lo.length - 1])
             setCurrentAttachment(ca.active)
        }

        if (!lo || lo.length == 0) {
            setCurrentAttachment(undefined)
        }
        
        setListOptions(lo)

    }, [fieldValue])

    return (
        <div className={classes.container}>
            <FormControl className={classes.controls} style={{ width: '100%' }}>
                <div style={{ width: '100%', display: 'flex' }}>
                    <Select
                        inputProps={{ tabIndex: fieldDefinition.tabIndex || -1 }}
                        value={currentAttachment ? currentAttachment.id : ""}
                        name={fieldDefinition.key}
                        error={error}
                        onChange={onDocumentSelectionChange}
                        className={hasHistoryPolicy ? classNames(classes.selector, classes.historyIndicator) : classes.selector}
                        MenuProps={{
                            PaperProps: {
                                style: { maxHeight: 300 }
                            }
                        }}
                    >
                        {listOptions && listOptions.map((c: any, idx: number) => {
                            var historyMessage
                            var history = getHistoryPolicy(idx)
                            if (history) {
                                historyMessage = history.title
                            }
                            var label = c.active.fileName
                            if (historyMessage) {
                                label = `${label} ${historyMessage}`
                            }
                            return (
                                <MenuItem
                                    style={{color: historyMessage ? "#1614b3" : "inherit"}}
                                    id={`doc_${idx}`}
                                    key={`doc_${idx}`}
                                    value={c.active.id}>
                                    {label}
                                </MenuItem>
                            );
                        })}

                    </Select>
                    {fieldDefinition.mode == "Required" && <i style={{ color: 'red', marginTop: 20 }}>*</i>}
                    {!readOnly && dropZone}
                </div>
            </FormControl>


            {!currentAttachment && 
                <div className={classes.emptySet}>
                    {(!listOptions || (listOptions && listOptions.length == 0)) && <b>There are no supporting documents</b>}
                </div>}

            {currentAttachment && 
                //<CurrentAttachmentContext.Provider value={[currentAttachment, setCurrentAttachment]}>
                    <ImageViewer 
                        onDelete={currentAttachment && currentAttachment._new ? removeAttachment : null}
                    />
                //</CurrentAttachmentContext.Provider>
            
            }

        </div>
    );

  };

  export default ImageList;